import React, { useEffect, useMemo, useState } from "react";

import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button } from "antd";

import PdfPreviewModal from "../../../../Common/UIComponents/PdfPreviewModal";

import { getFileFromS3WithPresigned } from "../../../../../utils/helpers";

import "./ShippingLabelModal.css";

const getFilename = (label, index) => {
  const tracking = label?.tracking_number || `label-${index + 1}`;
  return `shipping-label-${tracking}.pdf`;
};

const getLabelKey = (label, index) => `${label?.tracking_number || "label"}-${index}`;

const base64ToBlob = (base64) => {
  const cleaned = String(base64 || "").replace(/^data:application\/pdf;base64,/, "");
  if (!cleaned) return null;
  const binary = window.atob(cleaned);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: "application/pdf" });
};

const fetchLabelBlob = async (label, signal) => {
  if (label?.label_base64) {
    const fromBase64 = base64ToBlob(label.label_base64);
    if (fromBase64) return fromBase64;
  }

  // Prefer label_s3_path + presigned URL; label_url expires and must not be used.
  const s3Path = label?.label_s3_path;
  if (s3Path) {
    const fileBlobObj = await getFileFromS3WithPresigned([s3Path], "private", signal);
    const fromS3 = fileBlobObj?.[s3Path];
    if (fromS3) return new Blob([fromS3], { type: "application/pdf" });
  }

  return null;
};

export default function ShippingLabelModal({
  open,
  labels = [],
  onCancel,
  initialIndex = 0,
}) {
  const [activeKey, setActiveKey] = useState("");

  const files = useMemo(() => labels.map((label, index) => ({
    key: getLabelKey(label, index),
    fileName: getFilename(label, index),
    getSrc: (signal) => fetchLabelBlob(label, signal),
  })), [labels]);

  useEffect(() => {
    if (!open) return;
    const max = Math.max(labels.length - 1, 0);
    const index = Math.min(Math.max(initialIndex, 0), max);
    setActiveKey(getLabelKey(labels[index], index));
  }, [open, initialIndex, labels.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeIndex = Math.max(0, files.findIndex((file) => file.key === activeKey));
  const activeLabel = labels[activeIndex] || null;
  const hasMultiple = labels.length > 1;

  const subtitle = `${activeLabel?.tracking_number
    ? `Tracking ID: ${activeLabel.tracking_number}`
    : "Preview and print your shipping label"}${hasMultiple ? ` · ${activeIndex + 1} of ${labels.length}` : ""}`;

  return (
    <PdfPreviewModal
      open={open}
      title={(
        <span className="ShippingLabelModal__HeaderCopy">
          <span className="ShippingLabelModal__Title">Shipping label</span>
          <span className="ShippingLabelModal__Subtitle">{subtitle}</span>
        </span>
      )}
      onClose={onCancel}
      files={files}
      activeKey={activeKey}
      onActiveKeyChange={setActiveKey}
      errorMessage="Unable to preview this shipping label."
      width="40vw"
      footer={(
        <div className="ShippingLabelModal__ModalFooter">
          {hasMultiple ? (
            <div className="ShippingLabelModal__Pager">
              <Button
                type="text"
                className="ShippingLabelModal__PagerBtn"
                icon={<LeftOutlined aria-hidden />}
                disabled={activeIndex === 0}
                onClick={() => setActiveKey(getLabelKey(labels[activeIndex - 1], activeIndex - 1))}
              />
              <span className="ShippingLabelModal__PagerLabel">
                {`${activeIndex + 1} / ${labels.length}`}
              </span>
              <Button
                type="text"
                className="ShippingLabelModal__PagerBtn"
                icon={<RightOutlined aria-hidden />}
                disabled={activeIndex >= labels.length - 1}
                onClick={() => setActiveKey(getLabelKey(labels[activeIndex + 1], activeIndex + 1))}
              />
            </div>
          ) : (
            <span className="ShippingLabelModal__FooterHint">
              Attach this label to the top of your package
            </span>
          )}
        </div>
      )}
    />
  );
}
